/**
 * MentionEditor — a textarea-like TipTap editor whose only extra power is
 * @-mention pills over canonical tokens (see promptMentions.ts).
 *
 * The component's value is ALWAYS the plain-text storage format (tokens, not
 * editor JSON) — swapping this editor out later costs nothing. Rich-text
 * features are deliberately disabled; one paragraph per line, Enter = newline.
 */

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EditorContent, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { cn } from '@/lib/utils';
import {
  Mentionable,
  MentionableGroup,
  docToText,
  findMentionable,
  textToDoc,
} from './promptMentions';

interface MentionEditorProps {
  value: string;
  onChange: (value: string) => void;
  mentionables: Mentionable[];
  placeholder?: string;
  className?: string;
  /** Hide the "Type @ to reference…" footer hint (visible by default). */
  hideHint?: boolean;
}

interface MentionListProps {
  items: Mentionable[];
  command: (attrs: { id: string; label: string; group: MentionableGroup }) => void;
}

interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const GROUP_ORDER: MentionableGroup[] = [
  'Fields',
  'Questions',
  'AI Labels',
  'Visitor Data',
  'Account',
  'Skills',
  'Objectives',
  'Tools',
  'Team',
];

const GROUP_STYLES: Record<
  MentionableGroup,
  { pill: string; dot: string; menuBadge: string; header: string }
> = {
  Fields: {
    pill: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    dot: 'bg-sky-500',
    menuBadge: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    header: 'text-sky-700 dark:text-sky-300',
  },
  Questions: {
    pill: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    menuBadge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    header: 'text-emerald-700 dark:text-emerald-300',
  },
  'AI Labels': {
    pill: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
    menuBadge: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    header: 'text-amber-700 dark:text-amber-300',
  },
  'Visitor Data': {
    pill: 'bg-teal-500/15 text-teal-700 dark:text-teal-300',
    dot: 'bg-teal-500',
    menuBadge: 'bg-teal-500/15 text-teal-700 dark:text-teal-300',
    header: 'text-teal-700 dark:text-teal-300',
  },
  Account: {
    pill: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
    dot: 'bg-fuchsia-500',
    menuBadge: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
    header: 'text-fuchsia-700 dark:text-fuchsia-300',
  },
  Skills: {
    pill: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
    dot: 'bg-violet-500',
    menuBadge: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
    header: 'text-violet-700 dark:text-violet-300',
  },
  Objectives: {
    pill: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
    dot: 'bg-orange-500',
    menuBadge: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
    header: 'text-orange-700 dark:text-orange-300',
  },
  Tools: {
    pill: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    dot: 'bg-rose-500',
    menuBadge: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
    header: 'text-rose-700 dark:text-rose-300',
  },
  Team: {
    pill: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
    dot: 'bg-indigo-500',
    menuBadge: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
    header: 'text-indigo-700 dark:text-indigo-300',
  },
};

function groupFromToken(token: string | undefined, group: string | undefined): MentionableGroup {
  if (group && GROUP_ORDER.includes(group as MentionableGroup)) return group as MentionableGroup;
  if (token?.startsWith('skill:')) return 'Skills';
  if (token?.startsWith('objective:')) return 'Objectives';
  if (token?.startsWith('tool:')) return 'Tools';
  if (token?.startsWith('context:account.')) return 'Account';
  if (token?.startsWith('context:')) return 'Visitor Data';
  if (token?.startsWith('user:')) return 'Team';
  return 'Fields';
}

function mentionPillClass(token: string | undefined, group: string | undefined): string {
  return GROUP_STYLES[groupFromToken(token, group)].pill;
}

const MentionList = forwardRef<MentionListRef, MentionListProps>(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [items]);

  const select = (index: number) => {
    const item = items[index];
    if (item) command({ id: item.token, label: item.label, group: item.group });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((selectedIndex + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        select(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="rounded-md border bg-popover p-2 text-xs text-muted-foreground shadow-md">
        No matches
      </div>
    );
  }

  let lastGroup: MentionableGroup | null = null;
  return (
    <div className="max-h-64 w-72 overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
      {items.map((item, index) => {
        const header = item.group !== lastGroup ? item.group : null;
        const styles = GROUP_STYLES[item.group];
        lastGroup = item.group;
        return (
          <div key={item.token}>
            {header && (
              <div className={cn('px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide', styles.header)}>
                {header}
              </div>
            )}
            <button
              type="button"
              className={cn(
                'flex w-full items-center rounded-sm px-2 py-1 text-left text-sm',
                index === selectedIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
              )}
              onMouseEnter={() => setSelectedIndex(index)}
              // Select on mousedown (preventing default) so the editor never
              // blurs — a click would land after focus loss.
              onMouseDown={(event) => {
                event.preventDefault();
                select(index);
              }}
            >
              <span className={cn('mr-2 h-2 w-2 shrink-0 rounded-full', styles.dot)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{item.label}</span>
                <span className="block truncate text-[10px] text-muted-foreground">{item.token}</span>
              </span>
              <span className={cn('ml-2 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium', styles.menuBadge)}>
                {item.group}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
});
MentionList.displayName = 'MentionList';

function sortByGroup(items: Mentionable[]): Mentionable[] {
  return [...items].sort(
    (a, b) =>
      GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group) ||
      a.label.localeCompare(b.label)
  );
}

// Discoverability hint wording per group, phrased for "Type @ to reference …".
const GROUP_HINT_NOUNS: Record<MentionableGroup, string> = {
  Fields: 'fields',
  Questions: 'questions',
  'AI Labels': 'AI labels',
  'Visitor Data': 'visitor data',
  Account: 'account data',
  Skills: 'skills',
  Objectives: 'objectives',
  Tools: 'tools',
  Team: 'teammates',
};

function buildHintText(mentionables: Mentionable[]): string {
  const present = GROUP_ORDER.filter((group) =>
    mentionables.some((item) => item.group === group)
  );
  if (present.length === 0) return '';
  // Collapse the three form-derived groups into one word to keep it short.
  const nouns: string[] = [];
  for (const group of present) {
    const noun =
      group === 'Questions' || group === 'AI Labels'
        ? present.includes('Fields')
          ? null
          : GROUP_HINT_NOUNS[group]
        : GROUP_HINT_NOUNS[group];
    if (noun && !nouns.includes(noun)) nouns.push(noun);
  }
  const list =
    nouns.length > 1 ? `${nouns.slice(0, -1).join(', ')}, or ${nouns[nouns.length - 1]}` : nouns[0];
  return `Type @ to reference ${list}`;
}

export default function MentionEditor({
  value,
  onChange,
  mentionables,
  placeholder,
  className,
  hideHint,
}: MentionEditorProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Latest mentionables for the suggestion closure (avoids stale capture).
  const mentionablesRef = useRef(mentionables);
  mentionablesRef.current = mentionables;

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        blockquote: false,
        bold: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        italic: false,
        listItem: false,
        orderedList: false,
        strike: false,
        underline: false,
        link: false,
      } as any),
      Placeholder.configure({ placeholder: placeholder || '' }),
      Mention.extend({
        name: 'promptMention',
        addAttributes() {
          return {
            id: {
              default: null,
              parseHTML: (element) => element.getAttribute('data-mention-token'),
            },
            label: {
              default: null,
              parseHTML: (element) => element.getAttribute('data-mention-label'),
            },
            group: {
              default: null,
              parseHTML: (element) => element.getAttribute('data-mention-group'),
            },
            // True when the token no longer resolves against the catalog
            // (referenced field/skill/objective was deleted or renamed).
            // Maintained by the catalog-sync effect; serialization ignores it.
            broken: {
              default: false,
              parseHTML: (element) => element.getAttribute('data-mention-broken') === 'true',
            },
          };
        },
      }).configure({
        deleteTriggerWithBackspace: true,
        renderText: ({ node }) => `@[${node.attrs.id}]`,
        renderHTML: ({ node }) => [
          'span',
          {
            class: node.attrs.broken
              ? 'inline rounded px-1 py-0.5 font-medium border border-dashed border-red-500/70 bg-red-500/10 text-red-700 dark:text-red-300'
              : `inline rounded px-1 py-0.5 font-medium ${mentionPillClass(
                  node.attrs.id,
                  node.attrs.group
                )}`,
            'data-mention-token': node.attrs.id,
            'data-mention-label': node.attrs.label,
            'data-mention-group': node.attrs.group,
            'data-mention-broken': node.attrs.broken ? 'true' : 'false',
            title: node.attrs.broken
              ? `Missing reference: "${node.attrs.label || node.attrs.id}" no longer exists. ` +
                'The AI will see only the raw identifier. Remove this mention or recreate the item.'
              : `${node.attrs.group || groupFromToken(node.attrs.id, undefined)}: ${
                  node.attrs.label || node.attrs.id
                }`,
          },
          `@${node.attrs.label || node.attrs.id}`,
        ],
        suggestion: {
          char: '@',
          items: ({ query }) => {
            const lowered = (query || '').toLowerCase();
            const matches = mentionablesRef.current.filter(
              (item) =>
                item.label.toLowerCase().includes(lowered) ||
                item.token.toLowerCase().includes(lowered)
            );
            return sortByGroup(matches).slice(0, 30);
          },
          render: () => {
            let component: ReactRenderer<MentionListRef, MentionListProps> | null = null;

            // Fixed-position at viewport coordinates from document.body —
            // nothing can clip it there (overflow-hidden cards, ScrollArea).
            // Dialogs must whitelist [data-mention-dropdown] in their
            // onInteractOutside handler so item clicks don't close them.
            const position = (clientRect?: (() => DOMRect | null) | null) => {
              const element = component?.element as HTMLElement | undefined;
              const rect = clientRect?.();
              if (!element || !rect) return;
              element.style.position = 'fixed';
              element.style.zIndex = '100';
              element.style.left = `${rect.left}px`;
              const height = element.offsetHeight;
              const fitsBelow = rect.bottom + height + 8 <= window.innerHeight;
              element.style.top = fitsBelow
                ? `${rect.bottom + 4}px`
                : `${Math.max(8, rect.top - height - 4)}px`;
            };

            // clientRect() recomputes from the live caret position, so
            // repositioning on scroll keeps the dropdown anchored to the
            // text. Capture phase is required: the builder scrolls inside a
            // nested ScrollArea whose scroll events don't bubble to window.
            let latestClientRect: (() => DOMRect | null) | null | undefined = null;
            const reposition = () => position(latestClientRect);

            const teardown = () => {
              document.removeEventListener('scroll', reposition, true);
              window.removeEventListener('resize', reposition);
              component?.element.remove();
              component?.destroy();
              component = null;
            };

            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                });
                (component.element as HTMLElement).setAttribute('data-mention-dropdown', 'true');
                document.body.appendChild(component.element);
                latestClientRect = props.clientRect;
                position(props.clientRect);
                document.addEventListener('scroll', reposition, true);
                window.addEventListener('resize', reposition);
              },
              onUpdate: (props) => {
                component?.updateProps(props);
                latestClientRect = props.clientRect;
                position(props.clientRect);
              },
              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  teardown();
                  return true;
                }
                return component?.ref?.onKeyDown(props) ?? false;
              },
              onExit: teardown,
            };
          },
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placeholder]
  );

  const editor = useEditor({
    extensions,
    content: textToDoc(value || '', mentionables),
    editorProps: {
      attributes: {
        class: cn(
          'min-h-[120px] w-full px-3 py-2 text-sm outline-none',
          '[&_p]:my-0 [&_.is-editor-empty:first-child]:before:text-muted-foreground',
          '[&_.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]',
          '[&_.is-editor-empty:first-child]:before:float-left',
          '[&_.is-editor-empty:first-child]:before:pointer-events-none',
          '[&_.is-editor-empty:first-child]:before:h-0'
        ),
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange(docToText(currentEditor.getJSON()));
    },
  });

  // External value changes (e.g. "Reset to default") update the editor; the
  // serialized-equality check prevents clobbering the cursor while typing.
  useEffect(() => {
    if (!editor) return;
    const current = docToText(editor.getJSON());
    if ((value || '') !== current) {
      editor.commands.setContent(textToDoc(value || '', mentionablesRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Sync existing pills with the catalog when it updates: refresh labels and
  // flag broken (unresolvable) tokens. The content is deserialized before
  // async catalog entries arrive (org users load after mount), so a
  // refreshed page would otherwise show fallback labels forever. A token
  // only counts as broken once the catalog holds at least one entry of its
  // group; before that we can't tell "still loading" from "deleted", so
  // async groups (Team, Objectives) never flash red while fetching.
  // Attrs-only transaction: serialized text is unchanged (docToText reads
  // only the token), selection is preserved, and history is skipped so undo
  // never restores a stale label.
  useEffect(() => {
    if (!editor) return;
    const { state } = editor;
    const tr = state.tr;
    let changed = false;
    state.doc.descendants((node, pos) => {
      if (node.type.name !== 'promptMention') return;
      const known = findMentionable(mentionables, node.attrs.id);
      const group = groupFromToken(node.attrs.id, node.attrs.group);
      const groupLoaded = mentionables.some((item) => item.group === group);
      const broken = !known && groupLoaded;
      const label = known?.label ?? node.attrs.label;
      const groupAttr = known?.group ?? node.attrs.group;
      if (
        node.attrs.label !== label ||
        node.attrs.group !== groupAttr ||
        Boolean(node.attrs.broken) !== broken
      ) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          label,
          group: groupAttr,
          broken,
        });
        changed = true;
      }
    });
    if (changed) {
      tr.setMeta('addToHistory', false);
      editor.view.dispatch(tr);
    }
  }, [editor, mentionables]);

  const hintText = hideHint ? '' : buildHintText(mentionables);

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'relative rounded-md border border-input bg-transparent shadow-sm',
        'focus-within:ring-1 focus-within:ring-ring',
        className
      )}
    >
      <EditorContent editor={editor} />
      {hintText && (
        <div className="pointer-events-none select-none border-t border-input/50 px-3 py-1 text-[10px] leading-4 text-muted-foreground/80">
          <span className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded bg-muted font-semibold text-muted-foreground">
            @
          </span>
          {hintText}
        </div>
      )}
    </div>
  );
}
